import React, { FC, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { ProductApi } from "../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../services/Api/api";

interface Props {
	isShown: boolean;
	onHide: () => void;
	onSuccessAction: (data: ProductApi) => void;
	productId: number;
}

interface FormProps {
	product_id: number;
}

const MergeProductModalForm: FC<Props> = ({ isShown, onHide, onSuccessAction, productId }) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const form = useForm<FormProps>();
	const { addFlash, addSuccessFlash } = useFlash();
	const {
		setError,
		formState,
		formState: { errors },
		handleSubmit,
		control,
	} = form;
	const params = {
		include: "category,connections,connections_full,custom_fields,custom_ids,tax,volumes,open_linkeds",
	};
	const [selectedProduct, setSelectedProduct] = useState<ProductApi | undefined>(undefined);

	const onSubmit = handleSubmit(async (data: FormProps) => {
		setLoading(true);
		try {
			const res = await api.organization().mergeProduct(productId, data.product_id, params);
			onSuccessAction(res);
			addSuccessFlash(t("lib:common.flash.completed"));
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const onSearchProduct = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const onChangeProduct = (obj: ProductApi) => {
		setSelectedProduct(obj);
	};

	return (
		<Modal onHide={onHide} show={isShown}>
			<FormDirty formState={formState} noValidate onSubmit={onSubmit} key="merge-product-form">
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.product.field.product_merging.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<span className="text-muted d-block mb-2">
						{t("modules.product.field.product_merging.help_text")}
					</span>
					<FormSelectGroup
						errors={errors}
						control={control}
						loadOptions={onSearchProduct}
						onChange={(_, obj) => onChangeProduct(obj)}
						name="product_id"
						getOptionLabel={(option) => {
							return option.product_type === "PACKAGE" ? (
								<PackageSearchSelectListItem label={option.label} />
							) : (
								option.label
							);
						}}
						getOptionValue={(opt) => opt.id}
						defaultValue={{
							label: selectedProduct?.name,
							value: selectedProduct?.id,
						}}
						label={t("common.word.product")}
						data-testid="product_id"
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("modules.product.action.merge.title")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default MergeProductModalForm;
